import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiCheckboxCircleLine, RiCloseLine, RiHistoryLine, RiSimCard2Line } from 'react-icons/ri';
import simService, { Sim } from '../../services/simService';
import simPackageService from '../../services/simPackageService';
import userService from '../../services/userService';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useCreateSim, useUpdateSim } from '../../hooks/mutations/useSimMutations';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryClient, queryKeys } from '../../queryClient';
import { SIMS_PATH, simDetailUrl } from '../../utils/deviceLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { parsePhoneNumbers } from '../../utils/simPhone';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import SimFormFields from '../../components/sim/SimFormFields';
import {
  SimFormData,
  SIM_FIELD_LABELS,
  SIM_VALUE_FORMATTERS,
  emptySimForm,
  simToForm,
  validateSimForm,
} from '../../components/sim/simForm';
import {
  DetailPageLayout,
  DetailSection,
  EntityActivityTimeline,
  RecordMetaCard,
  useDetailNavigation,
} from '../../components/detail';

/** queryKey รายตัวของซิม (อยู่ใต้ sims.all → mutation ของซิม invalidate ให้เอง) */
export const simDetailKey = (id: number) => [...queryKeys.sims.all, 'detail', id] as const;

/**
 * หน้าเพิ่ม/แก้ไขซิม — `/master-data/sims/new` · `/master-data/sims/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน SimPage · มติ PO 28 ต.ค. 2026 · ต้นแบบ CompanyFormPage)
 *
 * ตรรกะบันทึกยกมาจาก handleSave ของโมดัลเดิมครบ: กันกดซ้ำ ref (KD3 S2126) · หลายเบอร์ → POST /sims/bulk ·
 * รหัสซ้ำ → ออกรหัสใหม่ + เตือน · details.field จาก backend → ใต้ช่อง (S2118 ไม่ซ้อน toast generic) ·
 * If-Match (409 → โหลดค่าล่าสุดลงฟอร์มใหม่) · สถานะ RESERVED/ISSUED ล็อก (ตั้งโดยใบงาน/ใบเบิกเท่านั้น)
 */
const SimFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const mode = isEdit ? 'edit' : 'create';
  const simId = isEdit ? parseRouteId(id) : null;
  const { hasPermission } = usePermission();
  const { user, getUserId } = useCurrentUser();
  const { backTo, goTo } = useDetailNavigation(SIMS_PATH);
  // ข้อความสำเร็จแสดงเอง (ตอนรหัสซ้ำต้องไม่ขึ้น "สำเร็จ")
  const createSimMutation = useCreateSim({ skipSuccessMessage: true });
  const updateSimMutation = useUpdateSim();

  const [formData, setFormData] = useState<SimFormData>(() => emptySimForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeError, setCodeError] = useState<string>('');
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const simQuery = useQuery({
    queryKey: simDetailKey(simId ?? 0),
    queryFn: () => simService.getSim(simId!),
    enabled: isEdit && simId !== null,
    staleTime: 0, // หน้าแก้ไขต้องได้ token If-Match ล่าสุดเสมอ
    refetchOnWindowFocus: false,
  });
  const selectedSim: Sim | undefined = simQuery.data;

  // OBS-S1362-A: ผู้ลงทะเบียนจาก /users/performers (ไม่ใช่ /users/ ที่ต้อง users.view)
  const { data: usersData } = useQuery({ queryKey: ['users', 'performers'], queryFn: () => userService.getPerformers() });
  const { data: simPackagesData } = useQuery({ queryKey: ['simPackages', 'active'], queryFn: () => simPackageService.getActiveSimPackages() });
  const users = usersData || [];
  const simPackages = simPackagesData || [];

  const initializedFor = useRef<number | 'new' | null>(null);
  useEffect(() => {
    if (isEdit) {
      if (!selectedSim || initializedFor.current === selectedSim.id) return;
      initializedFor.current = selectedSim.id;
      const f = simToForm(selectedSim, 'edit');
      setFormData(f);
      setInitialSnapshot(JSON.stringify(f));
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    codeGeneratorService
      .getNextCodePreview('sim', true)
      .then((r) => r.next_code)
      .catch((error) => {
        console.error('Error getting preview code:', error);
        return '';
      })
      .then((code) => {
        // ผู้ลงทะเบียน default = ผู้ใช้ที่ล็อกอิน (เหมือนโมดัลเดิม)
        const f = emptySimForm(code, getUserId());
        setFormData(f);
        setInitialSnapshot(JSON.stringify(f));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, selectedSim]);

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify(formData) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);
  const cancelTo = isEdit && simId ? simDetailUrl(simId) : backTo;

  const afterSaved = (savedId?: number | null) => {
    setSaved(true);
    if (savedId && hasPermission('sims.view')) goTo(simDetailUrl(savedId), { replace: true });
    else goTo(backTo, { replace: true });
  };

  // KD3 S2126 (BUG-S2126-002): ดับเบิลคลิก "บันทึก" ยิงคำขอ 2 ครั้ง · ถือ ref ไว้จน mutation/bulk settle
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const releaseSave = () => { savingRef.current = false; setIsSaving(false); };

  const handleSave = async () => {
    if (savingRef.current) return;
    const errors = validateSimForm(formData, mode, selectedSim);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTimeout(() => {
        const first = Object.keys(errors)[0];
        let el = document.querySelector(`[name="${first}"]`) as HTMLElement | null;
        // SearchableSelect ใช้ hidden input → โฟกัสปุ่มแทน
        if (el && el.tagName === 'INPUT' && el.getAttribute('type') === 'hidden') {
          el = (document.querySelector(`[data-name="${first}"]`) as HTMLElement | null) || el;
        }
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }, 50);
      return;
    }
    setFormErrors({});
    savingRef.current = true;
    setIsSaving(true);
    let handedOff = false;
    try {
      if (mode === 'edit' && selectedSim) {
        // BUG-S162-P1 rollout (S410): token ของแถวตอนเปิดฟอร์ม — มีคนแก้คั่นกลางจะได้ 409 แทนการทับ
        handedOff = true;
        updateSimMutation.mutate({ id: selectedSim.id, data: formData, ifMatch: (selectedSim as any).updated_at || (selectedSim as any).created_at }, {
          onSettled: releaseSave,
          onSuccess: () => {
            setCodeError('');
            afterSaved(selectedSim.id);
          },
          onError: (error: any) => {
            console.error('Error updating sim:', error);
            // useMutationWithRefresh แจ้งเตือนแล้ว · P-S601-1: 409 → โหลดค่าล่าสุดลงฟอร์มใหม่ (token ใหม่)
            if (error.response?.status === 409) {
              initializedFor.current = null;
              simQuery.refetch();
            }
          },
        });
        return;
      }

      // create — รองรับกรอกหลายเบอร์พร้อมกัน (textarea, คั่น , หรือขึ้นบรรทัด)
      const phones = parsePhoneNumbers(formData.phone_number);
      if (phones.length > 1) {
        // BULK: ส่ง phone_numbers + ข้อมูลร่วม (ตัด phone_number/serial_number/code — backend สร้างรหัสให้แต่ละซิม)
        const { phone_number, serial_number, code, ...shared } = formData as any;
        try {
          const result = await simService.createSimsBulk({ ...shared, phone_numbers: phones });
          showAlert('สำเร็จ', `สร้างซิม ${result.created_count} เบอร์สำเร็จ`, 'success');
          queryClient.invalidateQueries({ queryKey: queryKeys.sims.all });
          setSaved(true);
          goTo(backTo, { replace: true }); // หลายใบ = ไม่มีหน้าดูใบเดียว → กลับหน้ารายการ
        } catch (error: any) {
          console.error('Error bulk-creating sims:', error);
          const details = error?.response?.data?.error?.details;
          const invalid: string[] = details?.invalid_format || [];
          const dup: string[] = details?.duplicate_in_batch || [];
          const exists: string[] = details?.already_exists || [];
          if (invalid.length || dup.length || exists.length) {
            const parts: string[] = [];
            if (exists.length) parts.push(`• ซ้ำกับของเดิม: ${exists.join(', ')}`);
            if (dup.length) parts.push(`• ซ้ำในรายการที่กรอก: ${dup.join(', ')}`);
            if (invalid.length) parts.push(`• ต้องเป็นตัวเลข 10 หลัก: ${invalid.join(', ')}`);
            showAlert('พบเบอร์ที่มีปัญหา — ยังไม่ได้สร้างซิมใดเลย', parts.join('\n'), 'error');
          } else {
            showAlert('เกิดข้อผิดพลาด', error?.response?.data?.error?.user_message || 'ไม่สามารถสร้างซิมได้', 'error');
          }
        }
        return;
      }
      const singleData = { ...formData, phone_number: phones[0] ?? formData.phone_number };
      handedOff = true;
      createSimMutation.mutate(singleData, {
        onSettled: releaseSave,
        onSuccess: (created: Sim) => {
          setCodeError('');
          // KD3 S2121 (BUG-S2121-002): แสดงเสมอเมื่อสำเร็จ
          showAlert('สำเร็จ', 'สร้างซิมการ์ดสำเร็จ', 'success');
          afterSaved(created?.id);
        },
        onError: async (error: any) => {
          console.error('Error creating sim:', error);
          // S1218 FIX (BUG-S1218-004): handle 422 field_errors inline FIRST
          const fieldErrs = mapFieldErrors(error);
          if (fieldErrs) {
            setFormErrors(fieldErrs);
            focusFirstInvalidField(fieldErrs);
            return;
          }
          const errorMessage = error.response?.data?.error?.user_message ||
                               error.response?.data?.error?.message ||
                               error.response?.data?.detail || '';
          if (typeof errorMessage === 'string' && errorMessage.includes('รหัสซิมนี้มีอยู่ในระบบแล้ว')) {
            const oldCode = formData.code;
            try {
              const newCode = await codeGeneratorService.generateSingleCode('sim');
              setFormData((prev) => ({ ...prev, code: newCode }));
              setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
            } catch (genError) {
              console.error('Error generating new code:', genError);
              setCodeError('ไม่สามารถสร้างรหัสใหม่ได้ กรุณาลองใหม่');
            }
          } else {
            // KD3 S2118 (BUG-S2118-001): useMutationWithRefresh ขึ้น toast ข้อความจริงให้แล้ว — ห้ามซ้อน toast generic
            // backend บอก field มาด้วย (เช่นเบอร์ซ้ำ) → แสดงใต้ช่องนั้น
            const field = error.response?.data?.error?.details?.field;
            if (field && errorMessage) {
              setFormErrors(prev => ({ ...prev, [field]: errorMessage }));
            }
          }
        },
      });
    } finally {
      if (!handedOff) releaseSave();
    }
  };

  const title = isEdit ? 'แก้ไขข้อมูลซิมการ์ด' : 'เพิ่มซิมการ์ดใหม่';
  const breadcrumbs = useMemo(() => [
    { label: 'ซิมการ์ด', path: backTo },
    ...(isEdit && selectedSim ? [{ label: selectedSim.phone_number || selectedSim.code, path: simDetailUrl(selectedSim.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, selectedSim]);

  const notFound = isEdit && simId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiSimCard2Line />}
      title={title}
      recordId={isEdit ? selectedSim?.code || simId : undefined}
      subtitle={isEdit ? selectedSim?.phone_number : 'กรอกข้อมูลซิมการ์ด (รหัสสร้างอัตโนมัติ)'}
      backTo={cancelTo}
      loading={(isEdit && simQuery.isLoading) || (initialSnapshot === null && !notFound && !simQuery.isError)}
      error={notFound || simQuery.error}
      notFoundMessage="ไม่พบซิมที่ระบุ"
      onRetry={() => simQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && selectedSim ? (
          <>
            <RecordMetaCard
              createdAt={selectedSim.created_at}
              createdBy={selectedSim.created_by}
              updatedAt={selectedSim.updated_at}
              updatedBy={selectedSim.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline entityType="sim" entityId={selectedSim.id} fieldLabels={SIM_FIELD_LABELS} valueFormatters={SIM_VALUE_FORMATTERS} />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลซิมการ์ด" icon={<RiSimCard2Line />}>
        <div>
          <SimFormFields
            mode={mode}
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            codeError={codeError}
            simPackages={simPackages}
            users={users}
            currentUser={user as any}
            original={selectedSim ?? null}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {isSaving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default SimFormPage;
